import * as React from "react"
import { EditorThemeClasses, LexicalEditor, ParagraphNode } from "lexical"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { HeadingNode } from "@lexical/rich-text"
import { ToolbarPlugin } from "./plugins/ToolbarPlugin"
import RgptParagraphNode from "./CustomNodes/ParagraphNode"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { ListItemNode, ListNode } from "@lexical/list"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { AutoLinkNode, LinkNode } from "@lexical/link"
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin"
import cs from "classnames"
import AIPlugin from "./plugins/AIPlugin/AIPlugin"
import { SignaturePlugin } from "./plugins/SignaturePlugin/SignaturePlugin"
import { BeautifulMentionNode } from "lexical-beautiful-mentions"
import { WithParticleOverlay } from "../Forms/WithParticleOverlay"
import { VariablesPlugin } from "./plugins/VariablesPlugin/VariablesPlugin"

import styles from "./TextEditor.module.scss"
import { SingleLinePlugin } from "./plugins/SingleLinePlugin"
import { FormLabel } from "../Forms/FormLabel"
import InlineImagePlugin from "./plugins/InlineImagePlugin"
import { InlineImageNode } from "./plugins/InlineImagePlugin/InlineImageNode"
import DragDropPaste from "./plugins/DragDropPastePlugin/DragDropPastePlugin"
import { TextEditorContextProvider } from "./TextEditor.context"
import { fixEditorHtml } from "./TextEditor.utils"

interface PlaceholderProps {
  className?: string
  noToolbar?: boolean
  placeholder?: string
}

function Placeholder({ placeholder = "Type here...", noToolbar, className }: PlaceholderProps) {
  return <div className={cs(styles.placeholder, { [styles.noToolbar]: noToolbar }, className)}>{placeholder}</div>
}

const theme: EditorThemeClasses = {
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
    strikethrough: "editor-text-strikethrough",
  },
  mark: "editor-text-mark",
  beautifulMentions: {
    "{{": {
      container: styles.variable,
      containerFocused: styles.variableFocused,
    },
  },
}

export interface TextEditorProps {
  className?: string
  initialText?: string
  isLoading?: boolean
  disabled?: boolean
  label?: string
  placeholder?: string
  error?: string
  imitateTyping?: boolean
  signature?: string | null
  hideToolbar?: boolean
  allowCreateVariables?: boolean
  allowSetVariableValues?: boolean
  allowCustomVariables?: boolean
  variableValues?: Record<string, string>
  classes?: {
    disabled?: string
    editorContainer?: string
    editorInput?: string
    signature?: string
    signatureOpen?: string
    linkEditor?: string
    placeholder?: string
    label?: string
  }
  dataTestid?: string
  singleLine?: boolean
  autoFocus?: boolean
  withImages?: boolean

  /**
   * These cannot be inserted into the editor.
   */
  disabledAssetTypes?: string[]

  onHtmlChange?: (html: string) => void
  onFocus?: () => void
  onBlur?: () => void

  /**
   * Will be called instead of internal variable insertion logic.
   */
  onVariableSelected?: (value: string) => void
  onVariableMenuOpen?: () => void
  onVariableMenuClose?: () => void
  variableOptionFilter?(variableOption: string): boolean
}

function TextEditor({
  className,
  imitateTyping,
  initialText,
  onHtmlChange,
  isLoading,
  disabled,
  label,
  placeholder,
  error,
  signature,
  hideToolbar = false,
  allowCreateVariables = false,
  allowSetVariableValues = false,
  allowCustomVariables = true,
  variableValues,
  classes,
  singleLine = false,
  autoFocus = true,
  withImages,
  disabledAssetTypes,
  dataTestid,
  onBlur,
  onFocus,
  onVariableSelected,
  onVariableMenuOpen,
  onVariableMenuClose,
  variableOptionFilter,
}: TextEditorProps) {
  const rootRef = React.useRef<HTMLDivElement>(null)
  const [isSignatureOpen, setSignatureOpen] = React.useState(false)
  const [isTyping, setIsTyping] = React.useState(imitateTyping || false)
  const handleEditorChange = React.useCallback(
    (_: unknown, editor: LexicalEditor) => {
      if (isLoading) return

      // Wait for next frame to make sure we pick up the most relevant HTML,
      // e.g. with applied inline styles after editor state update
      requestAnimationFrame(() => {
        editor.update(() => {
          onHtmlChange?.(fixEditorHtml(editor, singleLine))
        })
      })
    },
    [isLoading, onHtmlChange, singleLine]
  )

  const handleContentEditableKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) {
        e.preventDefault()
      }
    },
    [disabled]
  )

  return (
    <WithParticleOverlay id="overlay" classes={{ particlesClassName: styles.loadingOverlay }}>
      <div className={className} ref={rootRef}>
        {label && <FormLabel className={cs(styles.label, classes?.label)}>{label}</FormLabel>}

        <LexicalComposer
          initialConfig={{
            namespace: "rGPT",
            theme,
            onError: console.error,
            nodes: [
              HeadingNode,
              ListNode,
              ListItemNode,
              LinkNode,
              RgptParagraphNode,
              { replace: ParagraphNode, with: () => new RgptParagraphNode() },
              BeautifulMentionNode,
              AutoLinkNode,
              InlineImageNode,
            ],
          }}
        >
          <TextEditorContextProvider
            triggerOnChange={handleEditorChange}
            editorRootRef={rootRef}
            disabledAssetTypes={disabledAssetTypes}
          >
            <div
              className={cs(
                styles.editorContainer,
                {
                  [styles.generating]: isLoading || isTyping,
                  [styles.error]: !!error,
                  [styles.singleLine]: singleLine,
                  [styles.disabled]: disabled,
                  [classes?.disabled ?? ""]: disabled,
                },
                classes?.editorContainer
              )}
            >
              <div className={cs(styles.editorInner)}>
                <ToolbarPlugin
                  setIsTyping={setIsTyping}
                  initialText={initialText}
                  imitateTyping={imitateTyping}
                  isHidden={hideToolbar}
                  allowCreateVariables={allowCreateVariables}
                  allowSetVariableValues={allowSetVariableValues}
                  linkEditorClassName={classes?.linkEditor}
                  onBlur={onBlur}
                  onVariableSelected={onVariableSelected}
                  onVariableMenuOpen={onVariableMenuOpen}
                  onVariableMenuClose={onVariableMenuClose}
                  withImages={withImages}
                />
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable
                      disabled={isLoading}
                      className={cs(
                        styles.editorInput,
                        {
                          [classes?.signatureOpen || ""]: isSignatureOpen,
                          [styles.singleLineInput]: singleLine,
                        },
                        classes?.editorInput
                      )}
                      onFocus={onFocus}
                      id="editor-input"
                      data-testid={dataTestid}
                      onKeyDown={handleContentEditableKeyDown}
                    />
                  }
                  placeholder={
                    <Placeholder placeholder={placeholder} noToolbar={hideToolbar} className={classes?.placeholder} />
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
                <SignaturePlugin signature={signature} className={classes?.signature} onOpenChange={setSignatureOpen} />
                {(isLoading || isTyping) && ( // animation
                  <div className={styles.ocean}>
                    <div className={styles.wave} />
                    <div className={styles.wave} />
                    <div className={styles.wave} />
                  </div>
                )}
                <AIPlugin isGenerating={isLoading || isTyping} />
                <ListPlugin />
                <LinkPlugin />
                {autoFocus && <AutoFocusPlugin />}
                <HistoryPlugin />
                <OnChangePlugin onChange={handleEditorChange} />
                <VariablesPlugin
                  allowCreateVariables={allowCreateVariables}
                  allowSetVariableValues={allowSetVariableValues}
                  variableValues={variableValues}
                  rootEditorElement={rootRef.current}
                  variableOptionFilter={variableOptionFilter}
                  allowCustomVariables={allowCustomVariables}
                />
                {withImages && (
                  <>
                    <InlineImagePlugin />
                    <DragDropPaste />
                  </>
                )}
                {singleLine && <SingleLinePlugin />}
              </div>
            </div>
          </TextEditorContextProvider>
        </LexicalComposer>
        {error && <span className={styles.errorLabel}>{error}</span>}
      </div>
    </WithParticleOverlay>
  )
}

const ClientSideTextEditor = (props: TextEditorProps) => {
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => {
    setMounted(true)
  }, [])
  if (mounted) return <TextEditor {...props} />
  return null
}
export { ClientSideTextEditor as TextEditor }
